import React, { useMemo } from 'react';
import {
  Pencil,
  Trash2,
  Users,
  MapPin,
  Clock,
  CalendarDays,
  AlertCircle,
  TrendingUp,
  Receipt,
  Target,
  Home
} from 'lucide-react';
import { computeTeamMonthStats, calculateTeamRating } from '../../utils/workerStatsUtils';
import { getRatingBarColorClass, getRatingColorClass } from '../../utils/workerRatingUtils';
import WorkerMonthPicker from '../workers/WorkerMonthPicker';

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('sr-RS');

const StatCell = ({ icon: Icon, label, value, sub, iconClass = 'text-gray-400' }) => (
  <div className="rounded-md bg-gray-900/45 border border-gray-700/50 px-2 py-1.5 min-w-0">
    <div className="flex items-center gap-1 mb-0.5">
      <Icon className={`h-3 w-3 flex-shrink-0 ${iconClass}`} />
      <span className="text-[10px] text-gray-500 truncate">{label}</span>
    </div>
    <p className="text-xs font-semibold text-white tabular-nums truncate">{value}</p>
    {sub && <p className="text-[10px] text-gray-500 truncate">{sub}</p>}
  </div>
);

const TeamStatsCard = ({
  team,
  workers,
  archiveJobs,
  monthKey,
  onMonthChange,
  ratingParams,
  getMemberNames,
  getMemberSharesDisplay,
  onEdit,
  onDelete
}) => {
  const stats = useMemo(
    () => computeTeamMonthStats(team, workers, archiveJobs, monthKey),
    [team, workers, archiveJobs, monthKey]
  );

  const { score } = useMemo(
    () => calculateTeamRating(team, workers, archiveJobs, monthKey, ratingParams),
    [team, workers, archiveJobs, monthKey, ratingParams]
  );

  const teamInitials = (team.name || 'TM').substring(0, 2).toUpperCase();
  const memberNames = getMemberNames(team.members || team.workers);
  const sharesDisplay = getMemberSharesDisplay(team);
  const hasRatingData = stats.completedJobs > 0 || score > 0;

  return (
    <div className="bg-gray-800 rounded-lg border border-violet-500/25 shadow-lg overflow-hidden">
      <div className="flex justify-between items-start gap-2 p-3 border-b border-gray-700/60">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 bg-gradient-to-br from-violet-500 to-fuchsia-600">
            {teamInitials}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-gray-500 tracking-wide mb-0.5">Mesečni učinak tima</p>
            <h3 className="font-semibold text-sm text-white truncate">{team.name}</h3>
            <p className="text-[11px] text-gray-400 truncate">
              {stats.memberCount === 1 ? '1 član' : `${stats.memberCount} članova`}
              {team.description ? ` · ${team.description}` : ''}
            </p>
          </div>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => onEdit(team)}
            className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-gray-700 rounded transition-colors"
            title="Izmeni tim"
          >
            <Pencil size={15} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(team._id)}
            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-gray-700 rounded transition-colors"
            title="Obriši tim"
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      <div className="px-3 py-2 space-y-1.5 border-b border-gray-700/40 bg-gray-900/20">
        <div className="flex items-start gap-2 min-w-0 text-[11px] text-gray-300">
          <Users className="h-3.5 w-3.5 text-gray-500 flex-shrink-0 mt-0.5" />
          <span className="truncate" title={memberNames}>
            {memberNames}
          </span>
        </div>
        {sharesDisplay && (
          <p className="text-[10px] text-gray-500 pl-5 truncate" title={sharesDisplay}>
            <span className="text-purple-400">Udele: </span>
            {sharesDisplay}
          </p>
        )}
      </div>

      <div className="px-3 pt-2">
        <WorkerMonthPicker monthKey={monthKey} onChange={onMonthChange} />
      </div>

      <div className="px-3 py-2">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-[11px] text-gray-400">Rejting</span>
          <span className={`text-sm font-bold tabular-nums ${getRatingColorClass(score)}`}>
            {!hasRatingData ? '—' : `${score}/100`}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-900 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${getRatingBarColorClass(score)}`}
            style={{ width: `${!hasRatingData ? 0 : score}%` }}
          />
        </div>
      </div>

      <div className="px-3 pb-1 grid grid-cols-3 gap-1.5">
        <StatCell icon={CalendarDays} label="Završeni" value={stats.completedJobs} iconClass="text-blue-400" />
        <StatCell icon={MapPin} label="1. izlazak" value={stats.firstVisitJobs} iconClass="text-green-400" />
        <StatCell icon={Clock} label="Ponovni" value={stats.secondVisitJobs} iconClass="text-amber-400" />
        <StatCell icon={TrendingUp} label="Prihod" value={fmt(stats.totalRevenue)} iconClass="text-emerald-400" />
        <StatCell icon={Receipt} label="Troškovi" value={fmt(stats.totalExpenses)} iconClass="text-orange-400" />
        <StatCell icon={AlertCircle} label="Reklamacije" value={stats.complaintsCount} iconClass="text-amber-400" />
        <StatCell
          icon={Home}
          label="Adrese"
          value={stats.uniqueAddresses}
          sub={`${stats.activeDays} dana`}
          iconClass="text-violet-400"
        />
        <StatCell
          icon={Target}
          label="Kapacitet"
          value={`${stats.completedJobs}/${stats.monthMaxCapacity}`}
          iconClass="text-cyan-400"
        />
        <div className="rounded-md bg-gray-900/45 border border-gray-700/50 px-2 py-1.5 flex items-center justify-center">
          <span
            className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
              team.active !== false ? 'bg-green-900/40 text-green-400' : 'bg-red-900/40 text-red-400'
            }`}
          >
            {team.active !== false ? 'Aktivan' : 'Neaktivan'}
          </span>
        </div>
      </div>
      {stats.memberCount > 0 && (
        <p className="px-3 pb-3 text-[10px] text-gray-500 leading-snug">
          Statistika obuhvata samo poslove dodeljene timu — zajednički urađeni poslovi članova
          {memberNames && memberNames !== 'Nema dodeljenih radnika' ? ` (${memberNames})` : ''}.
        </p>
      )}
    </div>
  );
};

export default TeamStatsCard;
